import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import { BankDetailsFields } from "../forms/BankDetailsFields"
import type { BankDetailsFormValues } from "../schemas/BankDetailsSchema"
import { BankDetailsSchema } from "../schemas/BankDetailsSchema"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { CompanyPaymentTypeMethod } from "@/graphql/codegen/graphql"

interface EditBankDetailsDialogProps {
  onClose: () => void
  onSave: (bankDetails: BankDetailsFormValues) => void
  paymentMethod: CompanyPaymentTypeMethod
}

const EditBankDetailsDialog: React.FC<EditBankDetailsDialogProps> = ({ onClose, onSave, paymentMethod }) => {
  const methods = useForm<BankDetailsFormValues>({
    mode: "all",
    resolver: zodResolver(BankDetailsSchema),
    defaultValues: {
      swiftCode: paymentMethod.swiftCode || "",
      iban: paymentMethod.iban || "",
      routingNumber: paymentMethod.routingNumber || "",
      institutionNumber: paymentMethod.institutionNumber || "",
      accountNumber: paymentMethod.accountNumber || "",
    },
  })

  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = methods

  const canSave = isDirty && isValid

  const handleSave = (data: BankDetailsFormValues) => {
    onSave(data)
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogHeader title='Billing and Payment Information' onClose={onClose} />
      <FormProvider {...methods}>
        <DialogContent classes={{ root: "px-10" }}>
          <Typography variant='subtitle1' className='font-medium text-gray-700'>
            Bank Account Transaction Information
          </Typography>

          <Box>
            <BankDetailsFields />
          </Box>
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10 pt-4'>
          <Button appearance='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button appearance='contained' disabled={!canSave} onClick={handleSubmit(handleSave)}>
            Save
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}

export default EditBankDetailsDialog
